import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, diff, fmtLong, fmtShort, todayKey } from './dates.js';
import { fetchAvailability } from './api.js';
import AvailabilityCalendar from './AvailabilityCalendar.jsx';
import CalendarModal from './CalendarModal.jsx';
import SearchPropertyCard from './SearchPropertyCard.jsx';
import useAggregateCalendar, { suggestStays } from './useAggregateCalendar.js';

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function readParams(search) {
  const q = new URLSearchParams(search);
  const a = q.has('a') ? parseInt(q.get('a'), 10) : parseInt(q.get('g') || '2', 10);
  const c = q.has('a') ? parseInt(q.get('c') || '0', 10) : 0;
  return {
    in: q.get('in') || '',
    out: q.get('out') || '',
    a: Number.isFinite(a) && a > 0 ? a : 2,
    c: Number.isFinite(c) && c > 0 ? c : 0,
  };
}

function validate(p) {
  if (!ISO.test(p.in) || !ISO.test(p.out)) return 'Choose your check-in and check-out dates to search.';
  if (p.in >= p.out) return 'Check-out must be after check-in.';
  if (p.in < todayKey()) return 'Check-in cannot be in the past.';
  const nights = diff(p.in, p.out);
  if (nights > 60) return 'Stays must be 60 nights or fewer.';
  return null;
}

/* Preliminary verdicts straight from the (usually browser-cached) calendar
   data, so the split renders instantly while Guesty confirms exact prices.
   Same pipeline as the server: capacity -> booked -> minimum stay ->
   available; unknown dates are never free ('checking'). */
function preliminaryRows(homes, calHomes, p, party, nights) {
  const rows = [];
  for (const h of homes) {
    const cal = calHomes[h.slug];
    if (h.maxGuests < party) {
      rows.push({ slug: h.slug, status: 'capacity', maxGuests: h.maxGuests, pending: true });
      continue;
    }
    const lastNight = addDays(p.out, -1);
    if (!cal || !cal.loadedUntil || cal.loadedUntil < lastNight) {
      rows.push({ slug: h.slug, status: 'checking', pending: true });
      continue;
    }
    const overlap = [];
    for (let d = p.in; d < p.out; d = addDays(d, 1)) {
      if (cal.blocked.has(d)) overlap.push(d);
    }
    if (overlap.length) {
      rows.push({
        slug: h.slug,
        status: 'booked',
        overlapFrom: overlap[0],
        overlapTo: addDays(overlap[overlap.length - 1], 1),
        pending: true,
      });
      continue;
    }
    const minReq = cal.minNightsByDate[p.in] ?? cal.minNights;
    if (nights < minReq) {
      rows.push({ slug: h.slug, status: 'minimum_stay', minNights: minReq, pending: true });
      continue;
    }
    rows.push({ slug: h.slug, status: 'available', nights, pending: true });
  }
  return rows;
}

export default function SearchResults({ homes }) {
  const [params, setParams] = useState(() => readParams(window.location.search));
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Calendar selection in progress (check-in picked, checkout pending).
  const [draftIn, setDraftIn] = useState('');
  const [draftOut, setDraftOut] = useState('');
  const requestSeq = useRef(0);
  const stripDatesRef = useRef(null);

  const invalidReason = useMemo(() => validate(params), [params]);
  const party = params.a + params.c;
  const nights = invalidReason ? 0 : diff(params.in, params.out);

  const { aggregate, homes: calHomes, loadedUntil, error: calError, onNeedMonths } = useAggregateCalendar(party, draftIn, draftOut);
  const homesBySlug = useMemo(() => {
    const map = {};
    for (const h of homes) map[h.slug] = h;
    return map;
  }, [homes]);

  const runSearch = useCallback((p) => {
    if (validate(p)) { setResults(null); return; }
    const seq = ++requestSeq.current;
    setLoading(true);
    setFetchError(null);
    fetchAvailability(p.in, p.out, p.a, p.c)
      .then((d) => {
        if (seq !== requestSeq.current) return;
        setResults(d);
        setLoading(false);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setFetchError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => { runSearch(params); }, [params, runSearch]);

  // Keep the URL canonical and shareable without a navigation.
  const apply = useCallback((next) => {
    setResults(null); // back to preliminary verdicts while the search re-runs
    setParams(next);
    const q = '?in=' + encodeURIComponent(next.in) + '&out=' + encodeURIComponent(next.out) +
      '&a=' + next.a + '&c=' + next.c;
    window.history.replaceState(null, '', '/search/' + q);
  }, []);

  function handleCalendarChange(inDate, outDate) {
    setDraftIn(inDate);
    setDraftOut(outDate);
  }

  function handleCalendarDone() {
    setCalendarOpen(false);
    if (draftIn && draftOut) apply({ ...params, in: draftIn, out: draftOut });
  }

  function openCalendar() {
    setDraftIn(invalidReason ? '' : params.in);
    setDraftOut(invalidReason ? '' : params.out);
    setCalendarOpen(true);
  }

  function handleGuests(field, raw) {
    const v = parseInt(raw, 10);
    if (!Number.isFinite(v)) return;
    const next = { ...params, [field]: Math.max(field === 'a' ? 1 : 0, v) };
    apply(next);
  }

  // Rows on screen: authoritative results when they've landed, otherwise
  // instant preliminary verdicts from calendar data.
  const rows = useMemo(() => {
    if (results) return results.homes;
    if (invalidReason || !calHomes) return null;
    return preliminaryRows(homes, calHomes, params, party, nights);
  }, [results, invalidReason, calHomes, homes, params, party, nights]);

  const split = useMemo(() => {
    if (!rows) return null;
    const avail = [];
    const rest = [];
    for (const r of rows) {
      if (!homesBySlug[r.slug]) continue;
      (r.status === 'available' ? avail : rest).push(r);
    }
    return { avail, rest };
  }, [rows, homesBySlug]);

  const cardParams = { in: params.in, out: params.out, a: params.a, c: params.c, nights };

  // Alternative same-length stays for homes the dates don't fit — computed
  // from the calendar-all data already loaded for the strip's calendar.
  // Capacity misses can't be fixed by dates, and checking/could-not-check
  // homes have no settled verdict to suggest around.
  const altStays = useMemo(() => {
    if (!rows || !calHomes || invalidReason) return {};
    const out = {};
    for (const r of rows) {
      if (r.status !== 'booked' && r.status !== 'minimum_stay') continue;
      const h = calHomes[r.slug];
      if (h) out[r.slug] = suggestStays(h, params.in, nights);
    }
    return out;
  }, [rows, calHomes, invalidReason, params.in, nights]);

  const anyChecking = !!split && split.rest.some((r) => r.status === 'checking');

  const headline = useMemo(() => {
    if (invalidReason) return 'Search our homes';
    if (fetchError && !split) return 'We could not run that search';
    if (!split || anyChecking) return 'Checking availability…';
    if (split.avail.length === 0) return 'Nothing free for those exact dates';
    const n = split.avail.length;
    return n + ' of ' + (split.avail.length + split.rest.length) + ' homes ' + (n === 1 ? 'is' : 'are') + ' free for your dates';
  }, [invalidReason, fetchError, split, anyChecking]);

  const summary = invalidReason ? '' :
    fmtLong(params.in) + ' → ' + fmtLong(params.out) + ' · ' +
    nights + ' night' + (nights === 1 ? '' : 's') + ' · ' +
    params.a + ' adult' + (params.a === 1 ? '' : 's') +
    (params.c ? ', ' + params.c + ' child' + (params.c === 1 ? '' : 'ren') : '');

  const valueStyle = (filled) => ({
    fontSize: '14px',
    fontWeight: filled ? 600 : 400,
    color: filled ? '#2B2118' : '#B6A794',
  });

  return (
    <div className="shs-search">
      {/* Sticky editable search strip */}
      <div className="shs-search-strip">
        <div className="shs-property-calendar-field shs-search-strip-dates" ref={stripDatesRef} onClick={openCalendar}>
          <div className="shs-property-calendar-cell">
            <span className="shs-property-calendar-label">Check in</span>
            <span style={valueStyle(!invalidReason)}>{!invalidReason ? fmtShort(params.in) : 'Add date'}</span>
          </div>
          <div className="shs-property-calendar-cell">
            <span className="shs-property-calendar-label">Check out</span>
            <span style={valueStyle(!invalidReason)}>{!invalidReason ? fmtShort(params.out) : 'Add date'}</span>
          </div>
        </div>
        <label className="shs-search-strip-guests">
          Adults
          <input type="number" min="1" max="16" value={params.a} onChange={(e) => handleGuests('a', e.target.value)} />
        </label>
        <label className="shs-search-strip-guests">
          Children
          <input type="number" min="0" max="15" value={params.c} onChange={(e) => handleGuests('c', e.target.value)} />
        </label>
      </div>

      {/* Headline + summary */}
      <div className="shs-search-head" aria-live="polite">
        <h1 className="t-h1 shs-search-headline">{headline}</h1>
        {summary && <div className="shs-search-summary">{summary}</div>}
        {invalidReason && <div className="shs-search-invalid">{invalidReason}</div>}
        {loading && split && !invalidReason && (
          <div className="shs-search-confirming" role="status">Confirming exact prices…</div>
        )}
      </div>

      {fetchError && !loading && (
        <div className="shs-search-error" role="alert">
          {fetchError}{' '}
          <button type="button" className="shs-search-retry" onClick={() => runSearch(params)}>Try again</button>
        </div>
      )}

      {!split && loading && (
        <div className="shs-search-loading" role="status">Checking all our homes for your dates…</div>
      )}

      {split && (
        <>
          {split.avail.length > 0 && (
            <div className="shs-search-results">
              {split.avail.map((r) => (
                <SearchPropertyCard key={r.slug} result={r} home={homesBySlug[r.slug]} params={cardParams} />
              ))}
            </div>
          )}

          {split.avail.length === 0 && !anyChecking && !loading && (
            <div className="shs-search-empty">
              <p>
                None of our homes fits those exact dates for {party} guest{party === 1 ? '' : 's'}.
                A small date shift often opens one up.
              </p>
              <div className="shs-search-empty-actions">
                <button type="button" className="shs-search-empty-btn" onClick={openCalendar}>Change dates</button>
                <a href="/contact.html" className="shs-search-empty-link">Ask us — we may be able to help</a>
              </div>
            </div>
          )}

          {split.rest.length > 0 && (
            <div className="shs-search-unavailable">
              <div className="shs-search-unavailable-title">
                {split.avail.length > 0 ? 'Not available for these dates' : 'Our homes, and why these dates don’t fit'}
              </div>
              {split.rest.map((r) => (
                <SearchPropertyCard
                  key={r.slug}
                  result={r}
                  home={homesBySlug[r.slug]}
                  params={cardParams}
                  suggestions={altStays[r.slug]}
                  onPickDates={(s) => {
                    apply({ ...params, in: s.in, out: s.out });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <CalendarModal open={calendarOpen} onClose={() => setCalendarOpen(false)} anchorRef={stripDatesRef}>
        {calError ? (
          <div className="shs-calendar-card"><div className="shs-calendar-status">{calError}</div></div>
        ) : !aggregate ? (
          <div className="shs-calendar-card"><div className="shs-calendar-status">Loading calendar…</div></div>
        ) : (
          <AvailabilityCalendar
            title="Change your dates"
            minNights={aggregate.minNights}
            minNightsByDate={aggregate.minNightsByDate}
            blocked={aggregate.blocked}
            checkIn={draftIn}
            checkOut={draftOut}
            today={todayKey()}
            loadedUntil={loadedUntil}
            onNeedMonths={onNeedMonths}
            doneLabel="Search these dates"
            onChange={handleCalendarChange}
            onDone={handleCalendarDone}
            startAllowed={aggregate.canStart}
          />
        )}
      </CalendarModal>
    </div>
  );
}
